import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { SidebarBrand, SwitchAppList } from "@/components/AppSidebar";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home as HomeIcon,
  Bell,
  Mail,
  User,
  LogOut,
  Heart,
  MessageCircle,
  AtSign,
  ChevronDown,
  LayoutGrid,
  BookOpen,
  CheckCheck,
  Users,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { formatDistanceToNow } from "date-fns";

const getInitials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const timeAgo = (iso) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
};

const TYPE_META = {
  like:    { icon: Heart,          color: "text-rose-500",  bg: "bg-rose-50",   label: "liked your post"           },
  comment: { icon: MessageCircle,  color: "text-sky-500",   bg: "bg-sky-50",    label: "commented on your post"    },
  mention: { icon: AtSign,         color: "text-purple-500", bg: "bg-purple-50", label: "mentioned you"             },
};

function NotificationItem({ notif, onRead }) {
  const meta = TYPE_META[notif.type] || TYPE_META.comment;
  const Icon = meta.icon;

  return (
    <div
      data-testid={`notif-${notif.notification_id}`}
      onClick={() => onRead(notif)}
      className={`flex gap-3 px-5 py-4 border-b border-slate-100 cursor-pointer transition-colors hover:bg-slate-50/70 ${
        !notif.read ? "bg-amber-50/40" : ""
      }`}
    >
      {/* Avatar with icon overlay */}
      <div className="relative shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={notif.actor_picture} />
          <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-semibold">
            {getInitials(notif.actor_name)}
          </AvatarFallback>
        </Avatar>
        <span className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center ${meta.bg}`}>
          <Icon className={`w-2.5 h-2.5 ${meta.color}`} />
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 leading-snug">
          <span className="font-semibold">{notif.actor_name}</span>{" "}
          <span className="text-slate-600">{meta.label}</span>
        </p>
        {notif.post_preview && (
          <p className="text-xs text-slate-400 mt-0.5 truncate">
            "{notif.post_preview}"
          </p>
        )}
        <p className="text-[11px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
      </div>

      {!notif.read && (
        <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-amber-500" />
      )}
    </div>
  );
}

export default function CommunityNotifications({ user }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/community/notifications`);
      setNotifications(data);
    } catch {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleRead = async (notif) => {
    if (!notif.read) {
      try {
        await axios.post(`${API}/community/notifications/${notif.notification_id}/read`);
        setNotifications((prev) =>
          prev.map((n) => n.notification_id === notif.notification_id ? { ...n, read: true } : n)
        );
      } catch {}
    }
    // Navigate to the community feed, passing post id as state
    navigate("/community", { state: { openPostId: notif.post_id } });
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/community/notifications/read-all`);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success("All marked as read");
    } catch {
      toast.error("Failed to mark as read");
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } finally { navigate("/"); }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Group by date
  const today = new Date();
  const groups = [
    {
      label: "Today",
      items: notifications.filter((n) => {
        const d = new Date(n.created_at);
        return d.toDateString() === today.toDateString();
      }),
    },
    {
      label: "Earlier",
      items: notifications.filter((n) => {
        const d = new Date(n.created_at);
        return d.toDateString() !== today.toDateString();
      }),
    },
  ].filter((g) => g.items.length > 0);

  const NavItem = ({ icon: Icon, label, onClick, active = false, badge = 0 }) => (
    <button
      data-testid={`community-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full text-left group relative ${
        active ? "bg-amber-50 text-amber-700 font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <div className="relative">
        <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-amber-600" : "text-slate-400 group-hover:text-slate-600"}`} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="text-sm hidden xl:block">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex md:ml-[68px] xl:ml-[260px] justify-center">

        {/* LEFT SIDEBAR */}
        <aside className="hidden md:flex flex-col fixed top-0 left-0 h-screen w-[68px] xl:w-[260px] shrink-0 bg-white border-r border-slate-200 px-2 xl:px-4 py-4 overflow-y-auto z-40">
          {/* App Switcher */}
          <div className="h-16 shrink-0 flex items-center px-1 mb-1">
            <SidebarBrand brandLabel="Community" />
          </div>

          <SwitchAppList user={user} active="community" />

          <Separator className="mb-2" />

          <p className="px-2.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hidden xl:block">
            Current App
          </p>

          {/* Navigation */}
          <nav className="flex flex-col gap-0.5 flex-1 relative">
            <NavItem icon={HomeIcon} label="Home" onClick={() => navigate("/community")} />
            <NavItem icon={Bell} label="Notifications" active onClick={() => {}} badge={0} />
            <NavItem icon={Mail} label="Messages" onClick={() => navigate("/community/messages")} />
            <NavItem icon={User} label="Profile" onClick={() => navigate("/community/profile")} />
          </nav>

          <div className="my-2 border-t border-slate-100" />

          {/* User area */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="community-user-menu"
                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors w-full">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden xl:block text-left min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-52">
              <DropdownMenuItem onClick={() => navigate("/community/profile")} className="gap-2">
                <User className="w-4 h-4" />My Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="gap-2 text-red-600 focus:text-red-700 focus:bg-red-50">
                <LogOut className="w-4 h-4" />Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </aside>

        {/* MAIN COLUMN */}
        <div className="flex w-full max-w-5xl">
        <main className="flex-1 min-w-0 border-r border-slate-200 bg-white">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b border-slate-100 px-5 py-4 flex items-center justify-between">
            <h1 className="font-serif text-xl font-bold text-slate-900">Notifications</h1>
            {unreadCount > 0 && (
              <Button
                data-testid="mark-all-read-btn"
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 gap-1.5 text-xs font-medium"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications list */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Bell className="w-7 h-7 text-slate-300" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-slate-900 mb-1">
                All caught up
              </h3>
              <p className="text-slate-400 text-sm max-w-xs">
                You'll be notified here when someone likes, comments on, or mentions you in the community.
              </p>
            </div>
          ) : (
            <div data-testid="notifications-list">
              {groups.map((group) => (
                <div key={group.label}>
                  <div className="px-5 py-2 bg-slate-50 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {group.label}
                    </p>
                  </div>
                  {group.items.map((n) => (
                    <NotificationItem key={n.notification_id} notif={n} onRead={handleRead} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </main>

        {/* RIGHT SIDEBAR — minimal on notifications page */}
        <aside className="hidden lg:flex flex-col sticky top-0 h-screen w-[320px] shrink-0 px-4 py-4 overflow-y-auto bg-slate-50">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-serif font-semibold text-slate-900 text-sm mb-2">
              About notifications
            </h2>
            <ul className="space-y-2.5 text-sm text-slate-500">
              {[
                { icon: Heart,         color: "text-rose-500",   text: "When someone likes your post" },
                { icon: MessageCircle, color: "text-sky-500",    text: "When someone comments on your post" },
                { icon: AtSign,        color: "text-purple-500", text: "When someone mentions you with @name" },
              ].map(({ icon: Icon, color, text }) => (
                <li key={text} className="flex items-start gap-2.5">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
        </div>{/* max-w-5xl wrapper */}
      </div>

      {/* Mobile bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex items-center justify-around px-4 py-2 z-50">
        <button onClick={() => navigate("/community")} className="p-2 text-slate-400">
          <HomeIcon className="w-6 h-6" />
        </button>
        <button onClick={() => {}} className="p-2 text-amber-600">
          <Bell className="w-6 h-6" />
        </button>
        <button onClick={() => navigate("/community/messages")} className="p-2 text-slate-400">
          <Mail className="w-6 h-6" />
        </button>
        <button onClick={() => navigate("/community/profile")} className="p-2 text-slate-400">
          <User className="w-6 h-6" />
        </button>
      </nav>
    </div>
  );
}
